import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';

function hasStatus(error: unknown): error is FetchBaseQueryError {
  return Boolean(error) && typeof error === 'object' && 'status' in (error as object);
}

/**
 * Decides whether a rejected request result is worth reporting. Captures
 * genuinely unexpected failures (server errors, unparseable/odd responses) and
 * ignores the expected/noisy ones (auth, not-found, network, timeouts, aborts)
 * that are already handled in the UI.
 */
export function shouldReportRequestError(error: unknown): boolean {
  if (!hasStatus(error)) {
    return false;
  }

  const { status } = error;

  if (typeof status === 'number') {
    return status >= 500;
  }

  // String statuses: FETCH_ERROR (network) and TIMEOUT_ERROR are transient noise.
  return status === 'PARSING_ERROR' || status === 'CUSTOM_ERROR';
}

interface IRejectedQueryMeta {
  arg?: {
    endpointName?: string;
    originalArgs?: unknown;
  };
}

/**
 * Redux middleware that reports unexpected API failures to the error monitor
 * with endpoint context. The data layer (currently RTK Query) surfaces request
 * errors as `rejectedWithValue` actions, so this observes the whole API surface
 * from one place without touching individual endpoints — keeping the wiring
 * tech-agnostic at the call site.
 */
export const requestErrorMiddleware: Middleware = () => next => action => {
  if (isRejectedWithValue(action)) {
    const error = action.payload;

    if (shouldReportRequestError(error)) {
      const meta = action.meta as IRejectedQueryMeta | undefined;
      const endpointName = meta?.arg?.endpointName ?? 'unknown';
      const { status } = error as FetchBaseQueryError;

      Sentry.withScope(scope => {
        scope.setTag('request.endpoint', endpointName);
        scope.setTag('request.status', String(status));
        scope.setContext('request', {
          endpoint: endpointName,
          status,
          args: meta?.arg?.originalArgs,
          error,
        });
        Sentry.captureException(new Error(`Request "${endpointName}" failed with status ${status}`));
      });
    }
  }

  return next(action);
};
